import {
    Alert,
    Callout,
    DialogBody,
    H4,
    Intent,
    Switch
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import {
    CustomUpgrade,
    useCustomUpgrades
} from "in_queue/contexts/CustomUpgradesContext";
import { useCustomUpgradesStatus } from "in_queue/hooks/useCustomUpgradesStatus";
import { CUSTOM_UPGRADES_LOCKED_MESSAGE } from "in_queue/utils/customUpgradesStatus";
import { useEffect, useState } from "react";
import { CUSTOM_UPGRADE_STATUS_ARCHIVED } from "../CustomUpgrades/customUpgradeConstants";
import {
    CustomUpgradeSelection,
    CustomUpgradesTable
} from "../CustomUpgrades/CustomUpgradesTable";
import css from "./AddUpgradesPanel.module.scss";
import {
    CustomUpgradeRiskBanner,
    getRiskState,
    riskMessage
} from "./CustomUpgradeRiskBanner";

interface AddUpgradesPanelProps {
    readonly selectedUpgradeId: string | null;
    readonly setSelectedUpgradeId: (id: string | null) => void;
}

export const AddUpgradesPanel: React.FC<AddUpgradesPanelProps> = ({
    selectedUpgradeId,
    setSelectedUpgradeId
}) => {
    const status = useCustomUpgradesStatus();

    // Add-on not unlocked: replace the picker with the upsell message.
    if (status === "locked") {
        return (
            <DialogBody className={css["AddUpgradesPanel-body"]}>
                <div>
                    <H4>Add upgrades</H4>
                    <Callout intent={Intent.PRIMARY} icon={IconNames.LOCK}>
                        {CUSTOM_UPGRADES_LOCKED_MESSAGE}
                    </Callout>
                </div>
            </DialogBody>
        );
    }

    return (
        <DialogBody className={css["AddUpgradesPanel-body"]}>
            <div className={css["AddUpgradesPanel-picker"]}>
                <div className={css["AddUpgradesPanel-header"]}>
                    <H4>Add upgrades</H4>
                    <span className={css["AddUpgradesPanel-subtitle"]}>
                        Apply a custom portfolio upgrade to the model.
                    </span>
                </div>
                <CustomUpgradePicker
                    selectedUpgradeId={selectedUpgradeId}
                    setSelectedUpgradeId={setSelectedUpgradeId}
                    target="this scenario"
                />
            </div>
            <CustomUpgradeRiskBanner
                selectedUpgradeId={selectedUpgradeId}
                className={css["AddUpgradesPanel-warning"]}
            />
        </DialogBody>
    );
};

/** The custom upgrade table and its risk confirmation, without the step's frame and heading. */
export const CustomUpgradePicker: React.FC<
    AddUpgradesPanelProps & {
        /** What a risky upgrade is being added to, e.g. "this scenario". */
        readonly target: string;
    }
> = ({ selectedUpgradeId, setSelectedUpgradeId, target }) => {
    const customUpgrades = useCustomUpgrades();
    const [showNonValidated, setShowNonValidated] = useState(false);
    const [pendingUpgrade, setPendingUpgrade] = useState<CustomUpgrade | null>(
        null
    );

    const activeUpgrades = (
        customUpgrades === "loading" ? [] : customUpgrades
    ).filter((u) => u.status !== CUSTOM_UPGRADE_STATUS_ARCHIVED);

    const selectedUpgrade = activeUpgrades.find(
        (upgrade) => upgrade.id === selectedUpgradeId
    );
    const selectedRisk =
        selectedUpgrade === undefined ? null : getRiskState(selectedUpgrade);

    // A scenario already pointing at a non-validated upgrade would otherwise
    // hide its own selection behind the toggle.
    useEffect(() => {
        if (selectedRisk !== null) {
            setShowNonValidated(true);
        }
    }, [selectedRisk]);

    if (customUpgrades === "loading") {
        return <>Loading upgrades…</>;
    }

    const usableUpgrades = activeUpgrades.filter(
        (upgrade) => getRiskState(upgrade) === null
    );
    const numNonValidated = activeUpgrades.length - usableUpgrades.length;

    const selection: CustomUpgradeSelection = {
        selectedId: selectedUpgradeId,
        onSelect: (upgrade) => {
            if (upgrade.id === selectedUpgradeId) {
                setSelectedUpgradeId(null);
            } else if (getRiskState(upgrade) === null) {
                setSelectedUpgradeId(upgrade.id);
            } else {
                setPendingUpgrade(upgrade);
            }
        },
        selectableIds: new Set(activeUpgrades.map((upgrade) => upgrade.id))
    };

    const pendingRisk =
        pendingUpgrade === null ? null : getRiskState(pendingUpgrade);

    return (
        <>
            <CustomUpgradesTable
                upgrades={showNonValidated ? activeUpgrades : usableUpgrades}
                selection={selection}
                scrollable
            />

            {numNonValidated > 0 && (
                <div className={css["AddUpgradesPanel-footer"]}>
                    <Switch
                        className={css["AddUpgradesPanel-toggle"]}
                        checked={showNonValidated}
                        label={`Show non-validated (${numNonValidated})`}
                        onChange={() => setShowNonValidated(!showNonValidated)}
                    />
                </div>
            )}
            {pendingUpgrade !== null && pendingRisk !== null && (
                <Alert
                    isOpen
                    intent={Intent.WARNING}
                    icon={IconNames.WARNING_SIGN}
                    cancelButtonText="Cancel"
                    confirmButtonText="Use it anyway"
                    onCancel={() => setPendingUpgrade(null)}
                    onConfirm={() => {
                        setSelectedUpgradeId(pendingUpgrade.id);
                        setPendingUpgrade(null);
                    }}
                >
                    <p>{riskMessage(pendingUpgrade, pendingRisk)}</p>
                    <p>Add it to {target} anyway?</p>
                </Alert>
            )}
        </>
    );
};
